import React, { useState } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import YTPlayer from 'react-youtube'
import { FormattedMessage } from 'react-intl'
import { If, Iframe, LoadingWrapper, Unless } from '../..'
import ErrorLogoWithMessage from '../Error/LogoWithMessage'
import { PLAYER_TYPE_MAP, PLAYER_TYPES } from '../../../constants/features'

import styles from './Player.module.scss'

const playerPropTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  videoId: PropTypes.string.isRequired,
  videoType: PropTypes.oneOf(PLAYER_TYPES).isRequired,
  onReady: PropTypes.func,
  onError: PropTypes.func,
}

const propTypes = {
  ...playerPropTypes,
  forwardRef: PropTypes.any,
}

export const Player = React.forwardRef((props, ref) => (
  <PlayerWithRefForwarding forwardRef={ref} {...props} />
))

const PlayerWithRefForwarding = ({
  className,
  videoId,
  videoType,
  onReady,
  onError,
  forwardRef,
  ...restProps
}) => {
  const [ready, setReady] = useState(false)
  const [errored, setErrored] = useState(false)

  return (
    <LoadingWrapper
      className={classnames(styles.root, className)}
      done={ready || errored}
      alwaysShowChildren
    >
      <If condition={errored}>
        <div className={styles.errorMessageContainer}>
          <div className={styles.errorMessage}>
            <ErrorLogoWithMessage>
              <FormattedMessage
                id='Player.error'
                defaultMessage='Error returned from {videoType} after requesting video playback'
                values={{ videoType }}
              />
            </ErrorLogoWithMessage>
          </div>
        </div>
      </If>
      <Unless condition={errored}>
        {
          {
            [PLAYER_TYPE_MAP.YOUTUBE]: (
              <YouTube
                onReady={e => {
                  setReady(true)
                  onReady && onReady(e)
                }}
                onError={e => {
                  setErrored(true)
                  onError && onError(e)
                }}
                videoId={videoId}
                ref={forwardRef}
                {...restProps}
              />
            ),
            [PLAYER_TYPE_MAP.BOMBBOMB]: (
              <BombBomb
                videoId={videoId}
                onReady={() => setReady(true)}
                {...restProps}
              />
            ),
            [PLAYER_TYPE_MAP.WISTIA]: (
              <Wistia
                videoId={videoId}
                onReady={() => setReady(true)}
                {...restProps}
              />
            ),
            [PLAYER_TYPE_MAP.PITCHHUB]: (
              <Pitchhub
                videoId={videoId}
                onReady={() => setReady(true)}
                {...restProps}
              />
            ),
          }[videoType]
        }
      </Unless>
    </LoadingWrapper>
  )
}

const youTubeIFrameProps = {
  className: PropTypes.string,
  videoId: PropTypes.string.isRequired,
  opts: PropTypes.object,
}

const YouTube = React.forwardRef(
  ({ videoId, className, opts, ...restProps }, ref) => (
    <YTPlayer
      ref={ref}
      videoId={videoId}
      containerClassName={classnames(styles.ytContainer, className)}
      className={styles.iframe}
      opts={{
        width: '100%',
        height: '100%',
        ...opts,
      }}
      {...restProps}
    />
  )
)

const bombBombIFrameProps = {
  videoId: PropTypes.string.isRequired,
}

const BombBomb = ({ videoId, ...restProps }) => (
  <Iframe
    src={`https://bbemaildelivery.com/bbext/?p=vidEmbed&id=${videoId}`}
    title='BombBomb Player'
    aspect='4:3'
    {...restProps}
  />
)

const wistiaIFrameProps = {
  videoId: PropTypes.string.isRequired,
}

const Wistia = ({ videoId, ...restProps }) => (
  <Iframe
    src={`https://fast.wistia.net/embed/iframe/${videoId}?autoPlay=false`}
    title='Wistia Player'
    aspect='16:9'
    {...restProps}
  />
)

const pitchHubIFrameProps = {
  videoId: PropTypes.string.isRequired,
}

const Pitchhub = ({ videoId, ...restProps }) => (
  <Iframe
    className={styles.pitchhub}
    src={`https://player.pitchhub.com/en/public/player/iframe/${videoId}`}
    title='Pitchhub Player'
    aspect='16:9'
    {...restProps}
  />
)

Player.propTypes = playerPropTypes
PlayerWithRefForwarding.propTypes = propTypes
YouTube.propTypes = youTubeIFrameProps
BombBomb.propTypes = bombBombIFrameProps
Wistia.propTypes = wistiaIFrameProps
Pitchhub.propTypes = pitchHubIFrameProps

export default Player
